import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const customLineageService = {
  /**
   * Create a new custom table
   * @param {Object} tableData - Table metadata
   * @returns {Promise}
   */
  createCustomTable: async (tableData) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/lineage/custom-tables`, {
        full_name: tableData.full_name,
        name: tableData.name,
        description: tableData.description || null,
        schema_name: tableData.schema_name || null,
        database_name: tableData.database_name || null,
        source_system: tableData.source_system || null,
        owner: tableData.owner || null,
        columns: tableData.columns?.map(col => ({
          column_name: col.name,
          column_type: col.type,
          column_description: col.description || null
        })) || []
      });
      return response.data;
    } catch (error) {
      console.error('Error creating custom table:', error);
      throw error;
    }
  },

  /**
   * Update an existing custom table
   * @param {string} full_name - Fully qualified table name
   * @param {Object} tableData - Updated table metadata
   * @returns {Promise}
   */
  updateCustomTable: async (full_name, tableData) => {
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/lineage/custom-tables/${encodeURIComponent(full_name)}`,
        {
          name: tableData.name || null,
          description: tableData.description || null,
          schema_name: tableData.schema_name || null,
          database_name: tableData.database_name || null,
          source_system: tableData.source_system || null,
          owner: tableData.owner || null,
          columns: tableData.columns?.map(col => ({
            column_name: col.name,
            column_type: col.type,
            column_description: col.description || null
          })) || null
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating custom table:', error);
      throw error;
    }
  },

  /**
   * Delete a custom table and its associated lineage
   * @param {string} full_name - Fully qualified table name
   * @returns {Promise}
   */
  deleteCustomTable: async (full_name) => {
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/lineage/custom-tables/${encodeURIComponent(full_name)}`
      );
      return response.data;
    } catch (error) {
      console.error('Error deleting custom table:', error);
      throw error;
    }
  },

  /**
   * Create a custom lineage edge between two tables
   * @param {Object} lineageData - Lineage edge data
   * @returns {Promise}
   */
  createCustomLineage: async (lineageData) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/lineage/custom-lineage`, {
        source_full_name: lineageData.source_full_name,
        target_full_name: lineageData.target_full_name,
        description: lineageData.description || null,
        column_mappings: lineageData.column_mappings || []
      });
      return response.data;
    } catch (error) {
      console.error('Error creating custom lineage:', error);
      throw error;
    }
  },

  /**
   * Update a custom lineage edge
   * @param {number} lineage_id - Lineage edge ID
   * @param {Object} lineageData - Updated lineage data
   * @returns {Promise}
   */
  updateCustomLineage: async (lineage_id, lineageData) => {
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/lineage/custom-lineage/${lineage_id}`,
        {
          description: lineageData.description || null,
          column_mappings: lineageData.column_mappings || []
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating custom lineage:', error);
      throw error;
    }
  },

  /**
   * Delete a custom lineage edge
   * @param {number} lineage_id - Lineage edge ID
   * @returns {Promise}
   */
  deleteCustomLineage: async (lineage_id) => {
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/lineage/custom-lineage/${lineage_id}`
      );
      return response.data;
    } catch (error) {
      console.error('Error deleting custom lineage:', error);
      throw error;
    }
  },

  /**
   * List custom lineage for a specific table
   * @param {string} full_name - Fully qualified table name
   * @returns {Promise}
   */
  listCustomLineageForTable: async (full_name) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/lineage/custom-lineage`, {
        params: { table: full_name }
      });
      return response.data;
    } catch (error) {
      console.error('Error listing custom lineage:', error);
      throw error;
    }
  }
};

export default customLineageService;
